import React, { lazy } from 'react';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { Send, MessageCircle, Phone, Mail, MapPin } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
export function Contact() {
  const {
    t
  } = useLang();
  const MapEmbed = <div className="border border-neutral-200 overflow-hidden bg-neutral-50 h-[320px] md:h-full min-h-[320px] md:min-h-[400px]" data-id="element-49">
      <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2541.4!2d30.5234!3d50.4501!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40d4ce56b3c5b7b7%3A0x0!2z0JLQtdC70LjQutCwINCS0LDRgdC40LvRjNC60ZbQstGB0YzQutCwLCA3MiwgS3lpdiwgVWtyYWluZQ!5e0!3m2!1suk!2sua!4v1700000000000!5m2!1suk!2sua&q=вул.+Велика+Васильківська+72+Київ" width="100%" height="100%" style={{
      border: 0,
      display: 'block',
      minHeight: '320px'
    }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Офіс на карті" data-id="element-50" />
    </div>;
  return <Section id="contact" background="gray" data-id="element-51">
      {/* DESKTOP: contact info left, map right */}
      <div className="hidden md:grid md:grid-cols-2 gap-12 lg:gap-24 relative z-10" data-id="element-52">
        {/* Left: contact info */}
        <div data-id="element-53">
          <h2 className="text-3xl md:text-5xl font-bold text-black mb-6 md:mb-8" data-id="element-54">
            {t('contact_h2')}
          </h2>
          <p className="text-neutral-500 mb-8 md:mb-12 text-base md:text-lg font-light max-w-md" data-id="element-55">
            {t('contact_subtitle')}
          </p>

          <div className="space-y-6 md:space-y-8 mb-8 md:mb-12" data-id="element-56">
            <div data-id="element-57">
              <p className="text-xs font-bold text-black uppercase tracking-wider mb-2 md:mb-3 flex items-center gap-2" data-id="element-58">
                <Phone className="h-3 w-3" data-id="element-59" />
                {t('contact_phone_label')}
              </p>
              <a href="tel:+380500000000" className="text-xl md:text-2xl text-black hover:text-neutral-600 transition-colors block break-words" data-id="element-60">
                +380 50 000 00 00
              </a>
            </div>
            <div data-id="element-61">
              <p className="text-xs font-bold text-black uppercase tracking-wider mb-2 md:mb-3 flex items-center gap-2" data-id="element-62">
                <Mail className="h-3 w-3" data-id="element-63" />
                {t('contact_email_label')}
              </p>
              <a href="mailto:info@buhgalter.ua" className="text-xl md:text-2xl text-black hover:text-neutral-600 transition-colors block break-all" data-id="element-64">
                info@buhgalter.ua
              </a>
            </div>
            <div data-id="element-65">
              <p className="text-xs font-bold text-black uppercase tracking-wider mb-2 md:mb-3 flex items-center gap-2" data-id="element-66">
                <MapPin className="h-3 w-3" data-id="element-67" />
                {t('contact_office_label')}
              </p>
              <p className="text-xl md:text-2xl text-black" data-id="element-68">
                м. Київ, вул. Велика Васильківська, 72
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4" data-id="element-69">
            <Button variant="outline" className="flex-1 justify-between group text-sm md:text-base" onClick={() => window.open('https://t.me/buhgalter_ua', '_blank')} data-id="element-70">
              <span data-id="element-71">Telegram</span>
              <Send className="h-4 w-4 text-neutral-400 group-hover:text-black group-hover:translate-x-1 transition-all" data-id="element-72" />
            </Button>
            <Button variant="outline" className="flex-1 justify-between group text-sm md:text-base" onClick={() => window.open('https://wa.me/380500000000', '_blank')} data-id="element-73">
              <span data-id="element-74">WhatsApp</span>
              <MessageCircle className="h-4 w-4 text-neutral-400 group-hover:text-black group-hover:scale-110 transition-all" data-id="element-75" />
            </Button>
          </div>
        </div>

        {/* Right: map */}
        {MapEmbed}
      </div>

      {/* MOBILE: map first, then contact info */}
      <div className="md:hidden flex flex-col gap-8 relative z-10" data-id="element-76">
        {/* Map on top */}
        {MapEmbed}

        {/* Contact info below */}
        <div data-id="element-77">
          <h2 className="text-2xl font-bold text-black mb-4" data-id="element-78">
            {t('contact_h2')}
          </h2>
          <p className="text-neutral-500 mb-6 text-base font-light" data-id="element-79">
            {t('contact_subtitle')}
          </p>

          <div className="space-y-5 mb-7" data-id="element-80">
            <div data-id="element-81">
              <p className="text-xs font-bold text-black uppercase tracking-wider mb-1.5 flex items-center gap-2" data-id="element-82">
                <Phone className="h-3 w-3" data-id="element-83" />
                {t('contact_phone_label')}
              </p>
              <a href="tel:+380500000000" className="text-lg text-black hover:text-neutral-600 transition-colors block" data-id="element-84">
                +380 50 000 00 00
              </a>
            </div>
            <div data-id="element-85">
              <p className="text-xs font-bold text-black uppercase tracking-wider mb-1.5 flex items-center gap-2" data-id="element-86">
                <Mail className="h-3 w-3" data-id="element-87" />
                {t('contact_email_label')}
              </p>
              <a href="mailto:info@buhgalter.ua" className="text-lg text-black hover:text-neutral-600 transition-colors block break-all" data-id="element-88">
                info@buhgalter.ua
              </a>
            </div>
            <div data-id="element-89">
              <p className="text-xs font-bold text-black uppercase tracking-wider mb-1.5 flex items-center gap-2" data-id="element-90">
                <MapPin className="h-3 w-3" data-id="element-91" />
                {t('contact_office_label')}
              </p>
              <p className="text-lg text-black" data-id="element-92">
                м. Київ, вул. Велика Васильківська, 72
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3" data-id="element-93">
            <Button variant="outline" className="w-full justify-between group" onClick={() => window.open('https://t.me/buhgalter_ua', '_blank')} data-id="element-94">
              <span data-id="element-95">Telegram</span>
              <Send className="h-4 w-4 text-neutral-400 group-hover:text-black group-hover:translate-x-1 transition-all" data-id="element-96" />
            </Button>
            <Button variant="outline" className="w-full justify-between group" onClick={() => window.open('https://wa.me/380500000000', '_blank')} data-id="element-97">
              <span data-id="element-98">WhatsApp</span>
              <MessageCircle className="h-4 w-4 text-neutral-400 group-hover:text-black group-hover:scale-110 transition-all" data-id="element-99" />
            </Button>
          </div>
        </div>
      </div>
    </Section>;
}